import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { colourHex } from "@/lib/pascoColours";
import { formatDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Timer, User, FileText, Eye, Pencil, ArrowRight, Droplet, Layers,
  ChevronDown, ChevronRight, ChevronsUpDown, ChevronsDownUp,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Inline (expand-in-place) "Job & Invoice" review. Reads the SAME endpoints the Job page uses
// (/jobs/{id} + /jobs/{id}/charge-lines) so every total/value matches — one source of truth.
export default function JobInvoicePreview({ jobId, open, onOpenFull, onEditInvoice, onInvoiceSend, sending, refreshKey }) {
  const [job, setJob] = useState(null);
  const [cl, setCl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docketBusy, setDocketBusy] = useState(false);
  const [openDays, setOpenDays] = useState({});

  useEffect(() => {
    if (!open || !jobId) return;
    setLoading(true); setJob(null); setCl(null);
    Promise.all([
      api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)),
      api.get(`/jobs/${jobId}/charge-lines`).then((r) => setCl(r.data)).catch(() => setCl(null)),
    ]).finally(() => setLoading(false));
  }, [open, jobId, refreshKey]);

  if (!open) return null;

  const downloadDocket = async () => {
    setDocketBusy(true);
    try {
      const res = await api.get(`/jobs/${jobId}/docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${job?.job_number || "docket"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate docket PDF"); }
    finally { setDocketBusy(false); }
  };

  const fin = cl?.financials;
  const materialsUsed = job?.materials_consumed || [];
  const beforePhotos = job?.before_photos || [];
  const afterPhotos = job?.after_photos || [];

  return (
    <div data-testid="job-invoice-preview" className="px-5 pb-5 border-t border-slate-100 pt-4 bg-slate-50/40">
      {loading || !job ? (
        <p className="text-slate-400 text-sm py-6 text-center">Loading job…</p>
      ) : (
        <div className="space-y-5 text-sm">
          {/* Service completion — one card PER day docket (multi-day jobs show every day's notes) */}
          {(() => {
            const dks = (job.dockets || []).filter((d) => !d.archived && !(d.office_entry && d.notes_outstanding));
            const raw = dks.length ? dks : [{
              _fallback: true, visit_date: job.appointment_date, docket_number: job.job_number,
              technician_name: job.technician_name, time_on_site: job.time_on_site,
              description_of_works: job.description_of_works, customer_comments: job.customer_comments,
              materials_used: job.materials_used, signature: job.signature, technician_signature: job.technician_signature,
            }];
            // Descending date order (latest day first).
            const list = [...raw].sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""));
            const multi = list.length > 1;
            const expandAll = () => setOpenDays(Object.fromEntries(list.map((_, i) => [i, true])));
            const collapseAll = () => setOpenDays({});
            const dayHours = (d) => {
              const t = d.time_on_site || {};
              if (t.hours_on_site != null && t.hours_on_site !== "") return `${t.hours_on_site} hrs`;
              return "";
            };
            return (
              <div data-testid="preview-service-completions">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 flex items-center gap-1"><Droplet className="w-3.5 h-3.5" />Service Completion{multi ? ` · ${list.length} days` : ""}</h4>
                  {multi && (
                    <div className="flex gap-2">
                      <button data-testid="sc-expand-all" onClick={expandAll} className="text-xs font-semibold text-navy hover:underline flex items-center gap-1"><ChevronsUpDown className="w-3.5 h-3.5" />Expand all</button>
                      <button data-testid="sc-collapse-all" onClick={collapseAll} className="text-xs font-semibold text-slate-500 hover:underline flex items-center gap-1"><ChevronsDownUp className="w-3.5 h-3.5" />Collapse all</button>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {list.map((d, di) => {
                    const dtos = d.time_on_site;
                    const dmats = d.materials_used || [];
                    const isOpen = multi ? !!openDays[di] : true;
                    return (
                      <div key={di} data-testid={`preview-day-${di}`} className={multi ? "bg-white rounded-lg border border-slate-100 overflow-hidden" : ""}>
                        {multi && (
                          <button type="button" data-testid={`preview-day-toggle-${di}`} onClick={() => setOpenDays((s) => ({ ...s, [di]: !s[di] }))}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors">
                            <span className="flex items-center gap-2 text-sm font-bold text-navy min-w-0">
                              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                              <span className="truncate">{formatDate(d.visit_date)}{d.docket_number ? ` · ${d.docket_number}` : ""}</span>
                            </span>
                            <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">{d.technician_name || ""}{d.technician_name && dayHours(d) ? " · " : ""}{dayHours(d)}</span>
                          </button>
                        )}
                        {isOpen && (
                        <div className={multi ? "px-3 pb-3 pt-1 border-t border-slate-100" : ""}>
                        {d.technician_name && !multi && <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><User className="w-3.5 h-3.5" />{d.technician_name}</p>}
                        {dtos && (dtos.arrival_time || dtos.finish_time) && (
                          <p className="text-navy flex items-center gap-1.5 mb-2">
                            <Timer className="w-4 h-4 text-slate-400" />
                            {dtos.arrival_time || "—"} → {dtos.finish_time || "—"}
                            {dtos.break_minutes ? ` · ${dtos.break_minutes} min break` : ""}
                            {dtos.hours_on_site != null ? ` · ${dtos.hours_on_site} hrs` : ""}
                          </p>
                        )}
                        {d.description_of_works && <div className="mb-2"><p className="text-xs uppercase tracking-wider text-slate-400 mb-0.5">Description of Works</p><p className="text-slate-600 whitespace-pre-line">{d.description_of_works}</p></div>}
                        {d.customer_comments && <div className="mb-2"><p className="text-xs uppercase tracking-wider text-slate-400 mb-0.5">Customer Comments</p><p className="text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-2 border border-slate-100">{d.customer_comments}</p></div>}
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Sealant &amp; Colours Used</p>
                          {dmats.length === 0 ? <p className="text-slate-500">—</p> : (
                            <div className="space-y-2">
                              {dmats.map((m, mi) => (
                                <div key={mi} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                                  <p className="font-semibold text-navy">{m.product}</p>
                                  <div className="flex flex-wrap gap-1.5 mt-1">
                                    {(m.colours || []).map((c, ci) => (
                                      <span key={ci} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-1.5 pr-2 py-0.5 text-xs text-navy">
                                        <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300" style={{ background: c.hex || colourHex(m.product, c.name) }} />{c.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {(d.signature || d.technician_signature) && (
                          <div className="grid grid-cols-2 gap-4 mt-3">
                            {d.signature && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Customer Signature</p>{d.signature.path ? <img src={fileUrl(d.signature.path)} alt="customer sig" className="h-16 object-contain border border-slate-200 rounded-lg p-1 bg-white" /> : <span data-testid="auto-approved-badge" className="inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg px-2.5 py-1">Approved (auto)</span>}<p className="text-xs text-navy mt-1">{d.signature.signed_by}</p></div>}
                            {d.technician_signature && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Technician Signature</p><img src={fileUrl(d.technician_signature.path)} alt="tech sig" className="h-16 object-contain border border-slate-200 rounded-lg p-1 bg-white" /><p className="text-xs text-navy mt-1">{d.technician_signature.signed_by}</p></div>}
                          </div>
                        )}
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Charge codes / invoice lines with running totals */}
          {/* Charge Codes table removed from Weekly Billing review — it was a non-period-scoped full-job
              list that could mismatch the invoice. The authoritative, period-scoped lines & totals are in
              "Preview Invoice". */}
          {false && cl?.lines?.length > 0 && (
            <div data-testid="preview-charge-codes">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Charge Codes / Invoice Lines</h4>
              {cl.warning_count > 0 && (
                <div data-testid="preview-charge-warning" className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{cl.warning_count} line{cl.warning_count > 1 ? "s need" : " needs"} attention before invoicing — check highlighted rows below.</span>
                </div>
              )}
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <th className="text-left font-semibold px-3 py-2">Charge Code</th>
                      <th className="text-right font-semibold px-3 py-2">Qty</th>
                      <th className="text-left font-semibold px-2 py-2">Unit</th>
                      <th className="text-right font-semibold px-3 py-2">Rate</th>
                      <th className="text-right font-semibold px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cl.lines.map((l, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${l.unpriced || l.under_minimum ? "bg-amber-50/60" : ""}`} data-testid={`preview-charge-line-${i}`}>
                        <td className="px-3 py-2 text-navy font-medium">{l.description}</td>
                        <td className="px-3 py-2 text-right text-slate-600">
                          {l.qty}
                          {l.under_minimum && <span className="ml-1 inline-block text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1 py-0.5 align-middle" title={`Below minimum ${l.min_lm}lm`}>under min {l.min_lm}lm</span>}
                        </td>
                        <td className="px-2 py-2 text-slate-500">{l.unit || "—"}</td>
                        <td className="px-3 py-2 text-right text-slate-600">
                          {l.unpriced
                            ? <span className="inline-block text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5">$0 — unpriced</span>
                            : money(l.rate)}
                          {l.rate_overridden && !l.unpriced && <span className="ml-1 inline-block text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1 py-0.5 align-middle" title="Admin rate override">override</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-navy">{money(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50/60">
                      <td colSpan={4} className="px-3 py-2 text-right text-slate-500">Subtotal (ex GST)</td>
                      <td className="px-3 py-2 text-right font-semibold text-navy" data-testid="preview-subtotal">{money(cl.subtotal)}</td>
                    </tr>
                    <tr className="bg-slate-50/60">
                      <td colSpan={4} className="px-3 py-1 text-right text-slate-500">GST (10%)</td>
                      <td className="px-3 py-1 text-right text-slate-600">{money(cl.gst)}</td>
                    </tr>
                    <tr className="bg-slate-50/60 border-t border-slate-200">
                      <td colSpan={4} className="px-3 py-2 text-right font-semibold text-navy">Total (inc GST)</td>
                      <td className="px-3 py-2 text-right font-bold text-navy" data-testid="preview-total">{money(cl.total_inc_gst)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">Read-only — matches the invoice sent to Xero.</p>
            </div>
          )}

          {/* Materials used (internal costing) */}
          {materialsUsed.length > 0 && (
            <div data-testid="preview-materials-used">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2 flex items-center gap-1"><Layers className="w-3.5 h-3.5" />Materials Used <span className="normal-case font-normal text-slate-400">· internal costing</span></h4>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
                {materialsUsed.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`preview-material-${i}`}>
                    <span className="text-navy">{m.product_name}</span>
                    <span className="text-slate-500">{m.quantity} {m.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial summary — office only */}
          {fin && (
            <div data-testid="preview-profitability" className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="bg-navy/5 px-4 py-2 flex items-center justify-between">
                <p className="font-semibold text-navy text-sm">Financial Summary — Profitability</p>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Office only</p>
              </div>
              <div className="px-4 py-3 text-sm space-y-0.5">
                <div className="flex justify-between"><span className="text-slate-500">Revenue (ex GST)</span><span className="text-navy">{money(fin.revenue)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Labour {fin.labour_basis === "metre" ? `(${fin.metres}m @ metre rate)` : `(${fin.hours}h${fin.is_saturday ? " · Sat rate" : ""})`}</span><span className="text-red-500">{fin.labour_cost > 0 ? `-${money(fin.labour_cost)}` : money(0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="text-red-500">{fin.material_cost > 0 ? `-${money(fin.material_cost)}` : money(0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Overhead</span><span className="text-red-500">{fin.overhead_cost > 0 ? `-${money(fin.overhead_cost)}` : money(0)}</span></div>
                <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1"><span className="text-navy">Gross Operating Margin</span><span data-testid="preview-gross-profit" className={fin.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{money(fin.gross_profit)} ({fin.margin}%)</span></div>
              </div>
            </div>
          )}

          {/* Attachments */}
          <div data-testid="preview-attachments">
            <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Attachments</h4>
            <Button data-testid="preview-download-docket" size="sm" variant="outline" onClick={downloadDocket} disabled={docketBusy} className="bg-white">
              <FileText className="w-4 h-4 mr-1.5" />{docketBusy ? "Preparing…" : "Completed Docket PDF"}
            </Button>
            {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
              <div className="mt-3 space-y-2">
                {beforePhotos.length > 0 && (
                  <div><p className="text-xs text-slate-400 mb-1">Before ({beforePhotos.length})</p>
                    <div className="grid grid-cols-4 gap-2">{beforePhotos.map((p, i) => <a key={i} href={fileUrl(p.path)} target="_blank" rel="noreferrer"><img src={fileUrl(p.path)} alt={`before ${i}`} className="w-full h-16 object-cover rounded-lg border border-slate-200" /></a>)}</div>
                  </div>
                )}
                {afterPhotos.length > 0 && (
                  <div><p className="text-xs text-slate-400 mb-1">After ({afterPhotos.length})</p>
                    <div className="grid grid-cols-4 gap-2">{afterPhotos.map((p, i) => <a key={i} href={fileUrl(p.path)} target="_blank" rel="noreferrer"><img src={fileUrl(p.path)} alt={`after ${i}`} className="w-full h-16 object-cover rounded-lg border border-slate-200" /></a>)}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {onEditInvoice && (
              <Button data-testid="preview-edit-invoice" variant="outline" size="sm" onClick={() => onEditInvoice(job)} className="bg-white">
                <Pencil className="w-4 h-4 mr-1.5" />Edit Invoice
              </Button>
            )}
            {onOpenFull && (
              <Button data-testid="preview-open-full" variant="outline" size="sm" onClick={onOpenFull} className="bg-white">
                Open Full Job <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
            {onInvoiceSend && (
              <Button data-testid="preview-invoice-send" onClick={onInvoiceSend} disabled={sending} className="bg-navy hover:bg-navy-light ml-auto">
                <Eye className="w-4 h-4 mr-1.5" />{sending ? "Preparing…" : "Preview Invoice"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
